import { Default, selectPopulatePackageItem, selectSelectedSpecialPackage, selectSelectedSpecialPackageId, updateSelectedPackageField } from '../../../features/client/clientSlice'
import { useDispatch, useSelector } from 'react-redux'
import { MingcutePackageLine } from '../../../assets/icons/Icons'
import { formatCurrency, formatNumber } from '../../../utils/formatAmount'
import { PackageCard } from '../../layout/Admin/clients/ClientModalBasicPackage'
import { setTypeOfThirdModal } from '../../../features/modal/modalSlice'
import { GetSpecialPackageProducts } from '../../../features/client/clientInterface'

interface BasicSpecialPackageItemProps {
    pkg: PackageCard
    pkgItem: GetSpecialPackageProducts[]
    selectedPackageToView: Default
    handleSelectPackage: (id: number, name: string, isDefault: boolean, pkgItem: GetSpecialPackageProducts[], pkgAmount: number| null) => void
    handleViewPackage: (selectedPackage: Default, type: 'special' | 'basic') => void
}

export default function BasicSpecialPackageItem({ pkg, pkgItem, selectedPackageToView, handleSelectPackage, handleViewPackage }: BasicSpecialPackageItemProps) {
    
    const dispatch = useDispatch()
    const selectedPackageId = useSelector(selectSelectedSpecialPackageId)
    const selectedPackage = useSelector(selectSelectedSpecialPackage)
    const specialPackageItem = useSelector(selectPopulatePackageItem);
    const totalItem = pkg.items?.length ?? 0
    const selectedTotalItem = specialPackageItem?.length ?? 0
    const isUpdate = selectedPackage?.id === pkg.id;
    const specialProduct = isUpdate ? selectedTotalItem : totalItem

    const minimumAmount = pkg.amount ?? 0;
    const currentAmount = selectedPackage?.depositAmount ?? 0;
    const showDepositWarning = isUpdate && currentAmount < minimumAmount;

    const isSelectedPackage = selectedPackageId === pkg.id || pkg.type === 'basic'
    
    const defaultBtnStyle = 'py-1.5 text-xs rounded transition-colors duration-200'
    const isSelected = 'bg-green-600 text-white'
    const isNotSelected = 'bg-cbColor hover:bg-cbColor/90 text-white'
    const isSelectedSpecial = isSelectedPackage ? "ring-2 ring-green-500 border-green-500" : "ring-2 ring-gray-400 border-gray-400"

    return (
        <div key={pkg.id} className={`${isSelectedSpecial} flex flex-col justify-between rounded p-2 cursor-pointer transition-colors bg-white border-t-8 space-y-1.5`}>   
            { isUpdate ? (
                <>
                    <div className='space-y-1'>
                        <div className="flex justify-between flex-row-reverse items-center">
                            <MingcutePackageLine height={25} width={25} />
                            <p className="text-[13px] font-medium truncate">
                                {pkg.name}
                            </p>
                        </div>
                        <>
                            <div className='grid grid-cols-2 gap-2'>
                                <div className="col-span-2 grid grid-cols-1 gap-1">
                                    <p className="text-[13px] ">Deposit amount *</p>
                                    <input
                                        type='text'
                                        value={isUpdate ? formatNumber(selectedPackage.depositAmount) : formatNumber(pkg.amount)}
                                        onChange={(e) => dispatch(updateSelectedPackageField({ field: 'depositAmount', value: Number(e.target.value.replace(/,/g, "")) || 0 }))}
                                        className={`w-full text-xs rounded border border-gray-300 px-1 py-1 shadow-sm outline-none placeholder:text-gray-400 focus:border-black cursor-pointer`}
                                        disabled={!isUpdate}
                                    />
                                    {showDepositWarning && (
                                        <p className="text-[11px] text-red-600">
                                            Deposit amount must be equal to or greater than{" "}
                                            <span className="font-medium">{formatNumber(minimumAmount)}</span>.
                                        </p>
                                    )}
                                </div>
                                <div className="col-span-2 grid grid-cols-1 gap-1">
                                    <p className="text-[13px] ">Date deposit *</p>
                                    <input
                                        type='date'
                                        value={isUpdate ? selectedPackage?.depositDate : ''}
                                        onChange={(e) => dispatch(updateSelectedPackageField({ field: 'depositDate', value: e.target.value }))}
                                        className={`w-full text-xs rounded border border-gray-300 px-1 py-1 shadow-sm outline-none placeholder:text-gray-400 focus:border-black cursor-pointer`}
                                        disabled={!isUpdate}
                                    />
                                </div>
                                <div className="col-span-2 grid grid-cols-1 gap-1">
                                    <p className="text-[13px]">Receipt number *</p>
                                    <input
                                        type='text'
                                        value={isUpdate ? selectedPackage?.receiptNumber : ''}
                                        onChange={(e) => dispatch(updateSelectedPackageField({ field: 'receiptNumber', value: e.target.value }))}
                                        className={`w-full text-xs rounded border border-gray-300 px-1 py-1 shadow-sm outline-none placeholder:text-gray-400 focus:border-black cursor-pointer`}
                                        disabled={!isUpdate}
                                        placeholder='Enter receipt number'
                                    />
                                </div>
                            </div>
                        </>
                    </div>
                    <div className='flex flex-col space-y-2'>
                        <div className='flex flex-col space-y-1'>
                            <p className='text-xs font-medium'>Start (Date and time) *</p>
                            <div className='grid grid-cols-2 gap-1'>
                                <input
                                    className={`w-full text-xs rounded border border-gray-300 px-1 py-1 shadow-sm outline-none placeholder:text-gray-400 focus:border-black cursor-pointer`}
                                    type="date"
                                    value={isUpdate ? selectedPackage?.startDate : ''}
                                    onChange={(e) => dispatch(updateSelectedPackageField({ field: 'startDate', value: e.target.value }))}
                                    disabled={!isUpdate}
                                />
                                <input 
                                    className={`w-full text-xs rounded border border-gray-300 px-1 py-1 shadow-sm outline-none placeholder:text-gray-400 focus:border-black cursor-pointer`}
                                    type="time" 
                                    value={isUpdate ? selectedPackage?.startTime : ''}
                                    onChange={(e) => dispatch(updateSelectedPackageField({ field: 'startTime', value: e.target.value }))}
                                    disabled={!isUpdate}
                                />
                            </div>
                        </div>
                        <div className='flex flex-col space-y-1'>
                            <p className='text-xs font-medium'>End (Date and time) *</p>
                            <div className='grid grid-cols-2 gap-1'>
                                <input 
                                    className={`w-full text-xs rounded border border-gray-300 px-1 py-1 shadow-sm outline-none placeholder:text-gray-400 focus:border-black cursor-pointer`}
                                    type="date"
                                    value={isUpdate ? selectedPackage?.endDate : ''}
                                    onChange={(e) => dispatch(updateSelectedPackageField({ field: 'endDate', value: e.target.value }))}
                                    disabled={!isUpdate}
                                />
                                <input 
                                    className={`w-full text-xs rounded border border-gray-300 px-1 py-1 shadow-sm outline-none placeholder:text-gray-400 focus:border-black cursor-pointer`}
                                    type="time" 
                                    value={isUpdate ? selectedPackage?.endTime : ''}
                                    onChange={(e) => dispatch(updateSelectedPackageField({ field: 'endTime', value: e.target.value }))}
                                    disabled={!isUpdate}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="overflow-hidden rounded-md border border-gray-200">
                        <div className="flex items-center justify-between bg-gray-100 px-3 py-2">
                            <p className="text-xs font-medium text-gray-600">Product</p>
                            <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-green-100 px-2 text-xs font-semibold text-green-700">
                                {specialProduct}
                            </span>
                        </div>
                        <button
                            onClick={() => dispatch(setTypeOfThirdModal('viewUpdateSpecialPackageProduct'))}
                            className={`${defaultBtnStyle} w-full rounded-none rounded-b-md bg-amber-700 px-2 text-white hover:bg-amber-800`}
                        >
                            Update product
                        </button>
                    </div>
                </>
            ) : (
                <div className='space-y-1'>
                    <div className="flex justify-between flex-row-reverse items-center">
                        <MingcutePackageLine height={25} width={25} />
                        <p className="text-[13px] font-medium truncate">
                            {pkg.name}
                        </p>
                    </div>
                    {pkg.type === "basic" ? (
                        <div className="flex justify-between">
                            <p className="text-[13px]">Cups:</p>
                            <p className="text-[13px]">{pkg.cups}</p>
                        </div>
                    ) : (
                        <>
                            <div className="flex justify-between">
                                <p className="text-[13px]">Minimum amount:</p>
                                <p className="text-[13px]">
                                    {formatCurrency(pkg.amount ?? 0)}
                                </p>
                            </div>
                            <div className="flex justify-between">
                                <p className="text-[13px]">Product:</p>
                                <p className="text-[13px]">{totalItem}</p>
                            </div>
                        </>
                    )}
                </div>
            )}
            <div className="grid grid-cols-2 gap-1">
                { pkg.type === "basic" ? (
                    <>
                        <button
                            onClick={() => handleViewPackage(selectedPackageToView, 'basic')} 
                            className="col-span-2 py-1.5 bg-gray-300 hover:bg-slate-200 text-gray-800 border border-slate-300 text-xs rounded transition-colors duration-200"
                        >
                            View
                        </button>
                    </>
                ) : (
                    <>
                        <button 
                            disabled={true}
                            onClick={() => handleSelectPackage(pkg.id, pkg.name, true, pkgItem, pkg.amount)}
                            className={`disabled:cursor-not-allowed ${defaultBtnStyle} ${ selectedPackageId === pkg.id ? isSelected : isNotSelected }`}
                        >
                            {selectedPackageId === pkg.id ? 'Unselect' : 'Select'}
                        </button>
                        <button 
                            disabled={true}
                            onClick={() => isUpdate ? dispatch(setTypeOfThirdModal('viewSelectedSpecialProduct')) : handleViewPackage(selectedPackageToView, 'special')} 
                            className="disabled:cursor-not-allowed py-1.5 bg-gray-300 hover:bg-slate-200 text-gray-800 border border-slate-300 text-xs rounded transition-colors duration-200"
                        >
                            View
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}
